import { useAuth } from '../../utils/auth';
import { useState, useEffect, useCallback } from 'react';
import { permissionsApi, auth as authApi } from '../../utils/api';
import { Shield, Check, X, Minus } from 'lucide-react';
import toast from 'react-hot-toast';

const ROLE_LABELS = { admin:'Admin', manager:'Manager', staff:'Staff', gm:'GM', freelance:'Freelance' };
const ROLE_COLORS = { admin:'var(--red)', manager:'var(--amber)', staff:'var(--accent-light)', gm:'var(--teal)', freelance:'var(--text-3)' };

// Icône selon statut
function PermIcon({ effective, override, roleDefault }) {
  if (override === true)  return <Check size={14} color="var(--teal)" title="Accordé manuellement"/>;
  if (override === false) return <X size={14} color="var(--red)" title="Retiré manuellement"/>;
  if (roleDefault)        return <Check size={14} color="var(--text-3)" title="Accordé par le rôle"/>;
  return <Minus size={14} color="rgba(255,255,255,0.15)" title="Non accordé"/>;
}

export default function AdminPermissions() {
  const { can } = useAuth();
  if (!can('admin.users')) return <div className="page-body"><div className="empty-state card"><p>Accès non autorisé</p></div></div>;

  const [users, setUsers]         = useState([]);
  const [selectedUser, setUser]   = useState(null);
  const [permissions, setPerms]   = useState([]);
  const [pendingOverrides, setPending] = useState({});
  const [saving, setSaving]       = useState(false);

  useEffect(() => {
    authApi.users().then(r => setUsers(r.data.users));
  }, []);

  const loadUserPerms = useCallback(async (userId) => {
    try {
      const r = await permissionsApi.byUser(userId);
      setPerms(r.data.permissions);
      setPending({});
    } catch { toast.error('Erreur chargement'); }
  }, []);

  const selectUser = (user) => {
    setUser(user);
    loadUserPerms(user.id);
  };

  // Cycler : pas d'override → accorder → retirer → pas d'override
  const toggle = (code, current) => {
    setPending(prev => {
      const existing = prev[code];
      let next;
      if (existing === undefined) {
        // Pas encore modifié — basé sur l'état effectif actuel
        next = !current.effective ? true : false;
      } else if (existing === true)  { next = false; }
      else if (existing === false)   { next = null; }  // null = reset
      else { next = true; }
      return { ...prev, [code]: next };
    });
  };

  const save = async () => {
    if (!selectedUser) return;
    setSaving(true);
    try {
      await permissionsApi.setUser(selectedUser.id, { overrides: pendingOverrides });
      toast.success('Permissions sauvegardées');
      loadUserPerms(selectedUser.id);
    } catch { toast.error('Erreur'); }
    finally { setSaving(false); }
  };

  const reset = () => setPending({});

  // Grouper par catégorie
  const byCategory = {};
  for (const p of permissions) {
    if (!byCategory[p.category]) byCategory[p.category] = [];
    byCategory[p.category].push(p);
  }

  const hasPending = Object.keys(pendingOverrides).length > 0;

  return (
    <>
      <div className="page-header">
        <div>
          <h2>Gestion des droits</h2>
          <p>Permissions par rôle et personnalisation par utilisateur</p>
        </div>
      </div>

      <div className="page-body">
        <div style={{ display:'grid', gridTemplateColumns:'280px 1fr', gap:20, alignItems:'start' }}>

          {/* Colonne gauche — liste utilisateurs */}
          <div>
            {/* Légende des rôles */}
            <div className="card" style={{ marginBottom:16, padding:14 }}>
              <div style={{ fontWeight:600, fontSize:13, marginBottom:10 }}>Niveaux de rôles</div>
              {[
                ['admin',     '4 — Accès total'],
                ['manager',   '3 — Gestion équipe'],
                ['staff',     '2 — Opérations'],
                ['gm',        '2 — Sessions'],
                ['freelance', '1 — Disponibilités'],
              ].map(([role, desc]) => (
                <div key={role} style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6 }}>
                  <span className="badge" style={{ background:`${ROLE_COLORS[role]}22`, color:ROLE_COLORS[role], fontSize:11, minWidth:70 }}>
                    {ROLE_LABELS[role]}
                  </span>
                  <span style={{ fontSize:12, color:'var(--text-2)' }}>{desc}</span>
                </div>
              ))}
              <div style={{ marginTop:10, padding:'8px 10px', background:'var(--bg-elevated)', borderRadius:6, fontSize:11, color:'var(--text-3)' }}>
                <Check size={10} style={{display:'inline',marginRight:4}} color="var(--teal)"/>accordé par rôle &nbsp;
                <Check size={10} style={{display:'inline',marginRight:4}} color="var(--text-3)"/>accordé manuellement &nbsp;
                <X size={10} style={{display:'inline',marginRight:4}} color="var(--red)"/>retiré
              </div>
            </div>

            {/* Liste utilisateurs */}
            <div className="card" style={{ padding:0, overflow:'hidden' }}>
              {users.map(u => (
                <div key={u.id}
                  onClick={() => selectUser(u)}
                  style={{
                    display:'flex', alignItems:'center', gap:10, padding:'10px 14px',
                    cursor:'pointer', borderBottom:'1px solid var(--border)',
                    background: selectedUser?.id===u.id ? 'var(--bg-elevated)' : 'transparent',
                    borderLeft: selectedUser?.id===u.id ? '3px solid var(--accent)' : '3px solid transparent',
                  }}>
                  <div className="avatar" style={{ width:28, height:28, fontSize:11 }}>
                    {u.first_name?.[0]}{u.last_name?.[0]}
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontWeight:600, fontSize:13, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                      {u.first_name} {u.last_name}
                    </div>
                    <span style={{ fontSize:11, color:ROLE_COLORS[u.role] }}>{u.role}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Colonne droite — permissions */}
          <div>
            {!selectedUser ? (
              <div className="empty-state card">
                <Shield size={32} style={{ opacity:0.3 }}/>
                <p>Sélectionnez un utilisateur pour gérer ses droits</p>
              </div>
            ) : (
              <div>
                {/* En-tête */}
                <div className="card" style={{ marginBottom:16, display:'flex', alignItems:'center', gap:12 }}>
                  <div className="avatar" style={{ width:36, height:36, fontSize:14 }}>
                    {selectedUser.first_name?.[0]}{selectedUser.last_name?.[0]}
                  </div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontWeight:700, fontSize:15 }}>
                      {selectedUser.first_name} {selectedUser.last_name}
                    </div>
                    <span style={{ fontSize:12, color:ROLE_COLORS[selectedUser.role] }}>
                      {selectedUser.role}
                    </span>
                  </div>
                  {hasPending && (
                    <div style={{ display:'flex', gap:8 }}>
                      <button className="btn btn-secondary btn-sm" onClick={reset}>Annuler</button>
                      <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>
                        {saving ? <span className="spinner" style={{width:14,height:14}}/> : 'Sauvegarder'}
                      </button>
                    </div>
                  )}
                </div>

                {/* Permissions par catégorie */}
                {Object.entries(byCategory).map(([category, perms]) => (
                  <div key={category} className="card" style={{ marginBottom:12, padding:0, overflow:'hidden' }}>
                    <div style={{ padding:'10px 16px', borderBottom:'1px solid var(--border)',
                      fontWeight:600, fontSize:13, color:'var(--text-2)',
                      textTransform:'uppercase', letterSpacing:'0.06em' }}>
                      {category}
                    </div>
                    {perms.map(p => {
                      const pending = pendingOverrides[p.code];
                      const displayOverride = pending !== undefined ? pending : p.override;
                      const displayEffective = pending !== undefined
                        ? (pending === null ? p.role_default : pending)
                        : p.effective;

                      return (
                        <div key={p.code}
                          onClick={() => toggle(p.code, p)}
                          style={{
                            display:'flex', alignItems:'center', gap:12, padding:'10px 16px',
                            borderBottom:'1px solid var(--border)', cursor:'pointer',
                            background: pending !== undefined ? 'rgba(124,92,252,0.05)' : 'transparent',
                            transition:'background 0.1s',
                          }}
                          onMouseEnter={e => e.currentTarget.style.background='var(--bg-elevated)'}
                          onMouseLeave={e => e.currentTarget.style.background=pending!==undefined?'rgba(124,92,252,0.05)':'transparent'}>
                          <PermIcon
                            effective={displayEffective}
                            override={displayOverride}
                            roleDefault={p.role_default}
                          />
                          <div style={{ flex:1 }}>
                            <div style={{ fontSize:13, fontWeight: displayEffective ? 500 : 400,
                              color: displayEffective ? 'var(--text-1)' : 'var(--text-3)' }}>
                              {p.label}
                            </div>
                            <div style={{ fontSize:10, color:'var(--text-3)', marginTop:1 }}>
                              {p.code}
                              {p.role_default && (
                                <span style={{ marginLeft:8, color:'var(--text-3)' }}>
                                  · Par défaut pour {p.min_role}+
                                </span>
                              )}
                            </div>
                          </div>
                          {displayOverride !== null && displayOverride !== undefined && (
                            <span style={{ fontSize:10, padding:'2px 6px', borderRadius:4,
                              background: displayOverride ? 'rgba(0,210,180,0.15)' : 'rgba(255,77,109,0.15)',
                              color: displayOverride ? 'var(--teal)' : 'var(--red)' }}>
                              {displayOverride ? 'Accordé' : 'Retiré'} manuellement
                            </span>
                          )}
                          {pending !== undefined && (
                            <span style={{ fontSize:10, color:'var(--accent-light)' }}>●</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
